import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowLeft, Loader2, Settings2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGetProcurementById, useUpdateProcurementStatus } from '@/hooks/use-procurement';
import { useGetSupplierById } from '@/hooks/use-supplier';
import { useGetAgentById } from '@/hooks/use-agent';
import { useAuth } from '@/contexts/AuthContext';
import { ProcurementStatusStepper } from '@/components/procurements/ProcurementStatusStepper';
import { ProcurementDocuments } from '@/components/procurements/ProcurementDocuments';
import { ProcurementPaymentDetails } from '@/components/procurements/ProcurementPaymentDetails';
import { ProcurementQualityDetails } from '@/components/procurements/ProcurementQualityDetails';
import { ProcurementDeliveryDetails } from '@/components/procurements/ProcurementDeliveryDetails';
import { ProcurementActivityLog } from '@/components/procurements/ProcurementActivityLog';
import { ProcurementTelegramTab } from '@/components/procurements/ProcurementTelegramTab';
import { ProcurementItemsList } from '@/components/procurements/ProcurementItemsList';
import { AdminAdjustmentDialog } from '@/components/procurements/admin/AdminAdjustmentDialog';
import { handleStatusChange as notifyStatusChange } from '@/services/financeService';

const ProcurementDetail = () => {
  const { id: procurementId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  
  // Don't try to fetch if ID is invalid
  const shouldFetch = procurementId && procurementId !== 'new' && procurementId.length > 0;
  
  const { data: procurement, isLoading, error } = useGetProcurementById(procurementId || '');
  const updateStatusMutation = useUpdateProcurementStatus();
  
  // Fetch source details based on source type
  const { data: supplierData } = useGetSupplierById(
    procurement?.source_type === 'supplier' ? procurement.source_id || '' : ''
  );
  
  const { data: agentData } = useGetAgentById(
    procurement?.source_type === 'agent' ? procurement.source_id || '' : ''
  );

  const handleBack = () => {
    navigate('/procurement');
  };

  const handleAdvanceStatus = async (nextStatus: string) => {
    setIsUpdating(true);
    try {
      // Use the generic status change handler that includes Telegram notifications
      const result = await notifyStatusChange(
        procurementId,
        nextStatus,
        procurement?.status || 'initiated',
        updateStatusMutation
      );
      
      if (result) {
        queryClient.invalidateQueries({ queryKey: ['procurement', procurementId] });
      }
    } catch (error) {
      console.error('Error advancing status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle invalid ID case
  if (!shouldFetch || !procurementId) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Procurements
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <p className="text-lg font-medium">Invalid procurement ID</p>
            <p className="text-muted-foreground">Please provide a valid procurement ID.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Procurements
        </Button>
        <Skeleton className="h-12 w-3/4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!procurement || error) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Procurements
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <p className="text-lg font-medium">Procurement not found</p>
            <p className="text-muted-foreground">The procurement you're looking for doesn't exist or you don't have access to it.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate costs based on items if available, otherwise fallback to legacy fields
  const getTotalQuantity = () => {
    if (procurement.items && procurement.items.length > 0) {
      return procurement.items.reduce((total, item) => total + item.quantity, 0);
    }
    return procurement.total_quantity || 0;
  };

  const getProductCost = () => {
    if (procurement.items && procurement.items.length > 0) {
      return procurement.items.reduce((total, item) => total + (item.price_per_unit * item.quantity), 0);
    }
    return (procurement.costs?.price_per_unit || 0) * (procurement.total_quantity || 0);
  };

  const getProductsCount = () => {
    if (procurement.items && procurement.items.length > 0) {
      return procurement.items.length;
    }
    return procurement.item_count || 1;
  };

  const getProductsSummary = () => {
    if (procurement.items && procurement.items.length > 0) {
      if (procurement.items.length === 1) {
        return procurement.items[0].product_name;
      }
      return `${procurement.items.length} products`;
    }
    
    // Fallback to single product name if items not loaded
    return procurement.product_name || 'N/A';
  };

  const totalQuantity = getTotalQuantity();
  const productCost = getProductCost();
  const transportCost = (procurement.costs?.transport_cost_per_unit || 0) * totalQuantity;
  const loadingCost = (procurement.costs?.loading_unloading_cost_per_unit || 0) * totalQuantity;
  const agentCost = (procurement.costs?.agent_cost_per_unit || 0) * totalQuantity;
  const otherCost = (procurement.costs?.other_cost_per_unit || 0) * totalQuantity;
  
  // Get source details based on source type
  const getSourceDetails = () => {
    if (procurement.source_type === 'supplier' && supplierData) {
      return {
        name: supplierData.name,
        contact: supplierData.phone_number,
        address: supplierData.address || 'N/A',
      };
    } else if (procurement.source_type === 'agent' && agentData) {
      return {
        name: agentData.name,
        contact: agentData.phone_number,
        address: agentData.address || 'N/A',
      };
    } else {
      return {
        name: 'N/A',
        contact: 'N/A',
        address: 'N/A',
      };
    }
  };
  
  const sourceDetails = getSourceDetails();

  // Check if admin can adjust (only when pending_finance)
  const canAdminAdjust = user?.role === 'admin' && procurement.status === 'pending_finance';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Button variant="ghost" onClick={handleBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Procurements
        </Button>
        
        <div className="flex items-center gap-2">
          {canAdminAdjust && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdjustmentDialog(true)}
            >
              <Settings2 className="mr-2 h-4 w-4" />
              Adjust Data
            </Button>
          )}
          <Badge variant={procurement.status === 'completed' ? 'default' : 'outline'}>
            {procurement.status}
          </Badge>
          {isUpdating && <Loader2 className="h-4 w-4 animate-spin" />}
        </div>
      </div>
      
      {/* Admin Adjustment Dialog */}
      {canAdminAdjust && (
        <AdminAdjustmentDialog
          open={showAdjustmentDialog}
          onOpenChange={setShowAdjustmentDialog}
          procurement={procurement}
        />
      )}
      
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-2xl">
              {getProductsSummary()} - {totalQuantity.toFixed(2)} kg
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Product Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm font-medium">Products</p>
                    <p className="text-sm">{getProductsSummary()}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Total Quantity</p>
                    <p className="text-sm">{totalQuantity.toFixed(2)} kg</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Product Count</p>
                    <p className="text-sm">{getProductsCount()} {getProductsCount() === 1 ? 'product' : 'products'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Source Type</p>
                    <p className="text-sm capitalize">{procurement.source_type}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Expected Delivery</p>
                    <p className="text-sm">
                      {procurement.expected_delivery_date
                        ? format(new Date(procurement.expected_delivery_date), 'PP')
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Created Date</p>
                    <p className="text-sm">{format(new Date(procurement.created_at), 'PP')}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium capitalize">{procurement.source_type} Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm font-medium">Name</p>
                    <p className="text-sm">{sourceDetails.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Contact</p>
                    <p className="text-sm">{sourceDetails.contact}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Address</p>
                    <p className="text-sm">{sourceDetails.address}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Product Breakdown Section */}
            {procurement.items && procurement.items.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Product Breakdown</h3>
                <ProcurementItemsList 
                  items={procurement.items} 
                  showTitle={false}
                />
              </div>
            )}
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Cost Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="p-3 border rounded-md">
                  <p className="text-sm font-medium">Product Cost</p>
                  <p className="text-lg">Br{productCost.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    Total from all products
                  </p>
                </div>
                
                <div className="p-3 border rounded-md">
                  <p className="text-sm font-medium">Transport Cost</p>
                  <p className="text-lg">Br{transportCost.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    {procurement.costs?.transport_cost_per_unit?.toFixed(2) || '0.00'} per kg × {totalQuantity.toFixed(2)} kg
                  </p>
                </div>
                
                <div className="p-3 border rounded-md">
                  <p className="text-sm font-medium">Loading/Unloading Cost</p>
                  <p className="text-lg">Br{loadingCost.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    {procurement.costs?.loading_unloading_cost_per_unit?.toFixed(2) || '0.00'} per kg × {totalQuantity.toFixed(2)} kg
                  </p>
                </div>
                
                <div className="p-3 border rounded-md">
                  <p className="text-sm font-medium">Agent Cost</p>
                  <p className="text-lg">Br{agentCost.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    {procurement.costs?.agent_cost_per_unit?.toFixed(2) || '0.00'} per kg × {totalQuantity.toFixed(2)} kg
                  </p>
                </div>
                
                <div className="p-3 border rounded-md">
                  <p className="text-sm font-medium">Other Cost</p>
                  <p className="text-lg">Br{otherCost.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    {procurement.costs?.other_cost_description || 'No description'}
                  </p>
                </div>
                
                <div className="p-3 border rounded-md bg-primary/5">
                  <p className="text-sm font-medium">Total Amount</p>
                  <p className="text-lg font-semibold">Br{(productCost + transportCost + loadingCost + agentCost + otherCost).toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">
                    {procurement.costs?.vat_applicable ? 'VAT applicable' : 'VAT not applicable'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Payment Terms</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="p-3 border rounded-md">
                  <p className="text-sm font-medium">Payment Type</p>
                  <p className="text-sm capitalize">{procurement.finance?.payment_term_type || 'Upfront'}</p>
                </div>
                
                {procurement.finance?.payment_term_type === 'term' && (
                  <div className="p-3 border rounded-md">
                    <p className="text-sm font-medium">Payment Term Days</p>
                    <p className="text-sm">{procurement.finance?.payment_term_days || 0} days</p>
                  </div>
                )}
                
                {procurement.finance?.payment_due_date && (
                  <div className="p-3 border rounded-md">
                    <p className="text-sm font-medium">Payment Due Date</p>
                    <p className="text-sm">
                      {format(new Date(procurement.finance.payment_due_date), 'PP')}
                    </p>
                  </div>
                )}
                
                <div className="p-3 border rounded-md">
                  <p className="text-sm font-medium">Damage Return</p>
                  <p className="text-sm">
                    {procurement.finance?.supports_damage_return
                      ? `Yes (${procurement.finance.damage_return_percentage}%)`
                      : 'Not supported'}
                  </p>
                </div>
              </div>
            </div>
            
            {procurement.notes && (
              <div className="space-y-2">
                <h3 className="text-lg font-medium">Notes</h3>
                <p className="text-sm p-3 bg-muted rounded-md">{procurement.notes}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Status</h3>
              <ProcurementStatusStepper
                currentStatus={procurement.status}
                isUpdating={isUpdating}
                onAdvance={handleAdvanceStatus}
                userRole={user?.role}
                paymentTermType={procurement.finance?.payment_term_type}
              />
            </div>
          </CardContent>
        </Card>
        
        <Tabs defaultValue="documents">
          <TabsList>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="payment">Payment</TabsTrigger>
            <TabsTrigger value="quality">Quality</TabsTrigger>
            <TabsTrigger value="delivery">Delivery</TabsTrigger>
            <TabsTrigger value="activity">Activity Log</TabsTrigger>
            <TabsTrigger value="telegram">Telegram</TabsTrigger>
          </TabsList>
          
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle>Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <ProcurementDocuments procurementId={procurementId || ''} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="payment">
            <Card>
              <CardHeader>
                <CardTitle>Payment Details</CardTitle>
              </CardHeader>
              <CardContent>
                <ProcurementPaymentDetails procurement={procurement} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="quality">
            <Card>
              <CardHeader>
                <CardTitle>Quality Details</CardTitle>
              </CardHeader>
              <CardContent>
                <ProcurementQualityDetails procurement={procurement} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="delivery">
            <Card>
              <CardHeader>
                <CardTitle>Delivery Details</CardTitle>
              </CardHeader>
              <CardContent>
                <ProcurementDeliveryDetails procurement={procurement} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle>Activity Log</CardTitle>
              </CardHeader>
              <CardContent>
                <ProcurementActivityLog procurementId={procurementId || ''} />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="telegram">
            <ProcurementTelegramTab procurement={procurement} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProcurementDetail;
